import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Package, ShoppingBag, Users as UsersIcon, CreditCard,
  Settings as SettingsIcon, FileText, Mail, MessageCircle, LogOut, ArrowLeft, ChevronRight, Sparkles, Menu, X
} from "lucide-react";
import { LOGOS } from "@/data/site";
import { useAuth } from "@/context/AuthContext";

const ITEMS = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/orders", icon: ShoppingBag, label: "Orders" },
  { to: "/admin/payments", icon: CreditCard, label: "Payments" },
  { to: "/admin/users", icon: UsersIcon, label: "Users" },
  { to: "/admin/enquiries", icon: MessageCircle, label: "Product Enquiries" },
  { to: "/admin/franchise", icon: FileText, label: "Franchise Leads" },
  { to: "/admin/contact", icon: Mail, label: "Contact Inbox" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const Sidebar = (
    <aside className="w-[260px] shrink-0 bg-[#0c3a22] text-white min-h-screen flex flex-col">
      <div className="px-6 py-6 border-b border-white/10 flex items-center gap-3">
        <img src={LOGOS.native_touch} alt="Native Touch" className="w-9 h-9 rounded-full bg-[var(--gold)] p-0.5 object-contain" />
        <div>
          <div className="font-serif-display text-base leading-tight">Native Touch</div>
          <div className="kbd text-[0.6rem] text-[var(--gold-soft)]">Admin Console</div>
        </div>
      </div>
      <nav className="px-3 py-4 flex-1">
        {ITEMS.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-4 py-2.5 mb-1 rounded-md text-[13.5px] transition group ${
                isActive
                  ? "bg-[var(--gold)] text-[#0c3a22] font-semibold"
                  : "text-white/85 hover:bg-white/10"
              }`
            }
          >
            <it.icon size={17} strokeWidth={1.8} />
            <span>{it.label}</span>
            <ChevronRight size={14} className="ml-auto opacity-50" />
          </NavLink>
        ))}
      </nav>
      <div className="px-6 py-5 border-t border-white/10 text-sm">
        <div className="kbd text-[0.6rem] text-[var(--gold-soft)] mb-1.5">Signed in</div>
        <div className="font-semibold truncate">{user?.name}</div>
        <div className="text-white/65 text-xs truncate">{user?.email}</div>
        <div className="flex flex-col gap-1.5 mt-3">
          <Link to="/" className="inline-flex items-center gap-2 text-xs text-white/80 hover:text-[var(--gold)] transition">
            <ArrowLeft size={12} /> Back to Storefront
          </Link>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-xs text-red-300 hover:text-red-200 transition"
          >
            <LogOut size={12} /> Sign Out
          </button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="bg-[#f4ecdb] min-h-screen">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-50 flex items-center justify-between px-4 py-3 bg-[#0c3a22] text-white shadow">
        <div className="flex items-center gap-2">
          <img src={LOGOS.native_touch} alt="" className="w-7 h-7 rounded-full bg-[var(--gold)] p-0.5 object-contain" />
          <span className="font-serif-display text-sm">Admin</span>
        </div>
        <button
          aria-label="Open menu"
          onClick={() => setDrawerOpen(true)}
          className="p-1.5"
        >
          <Menu size={20} />
        </button>
      </div>

      <div className="flex">
        <div className="hidden lg:block">{Sidebar}</div>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-[60] flex">
            <div className="absolute inset-0 bg-black/60" onClick={() => setDrawerOpen(false)} />
            <div className="relative">{Sidebar}
              <button
                onClick={() => setDrawerOpen(false)}
                className="absolute top-4 right-4 text-white"
                aria-label="Close menu"
              >
                <X size={22} />
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0">
          <header className="hidden lg:flex items-center justify-between px-10 py-5 bg-white border-b border-[#e5dec9]">
            <div>
              <div className="overline overline-green text-[0.55rem]">Native Touch · Operations</div>
              <h1 className="font-serif-display text-2xl text-[var(--ink)] leading-tight mt-1">
                {ITEMS.find((i) => loc.pathname === i.to || (i.to !== "/admin" && loc.pathname.startsWith(i.to)))?.label || "Dashboard"}
              </h1>
            </div>
            <div className="inline-flex items-center gap-2 text-xs text-[var(--ink-3)]">
              <Sparkles size={13} className="text-[var(--gold)]" />
              Production data · Live
            </div>
          </header>
          <div className="p-5 md:p-10">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
